import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetTextInput,
  BottomSheetView,
} from '@gorhom/bottom-sheet'
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ScrollView, SectionList, View } from 'react-native'
import {
  Button,
  Checkbox,
  Icon,
  Text,
  TouchableRipple,
} from 'react-native-paper'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import UserAvatar from '@/components/navigation/UserAvatar'
import UniChip from '@/components/unistyles/UniChip'
import UniDivider from '@/components/unistyles/UniDivider'
import useWidescreenBottomSheetStyle from '@/hooks/useWidescreenBottomSheetStyle'

export type ReportPreferences = {
  recipientUserIds: string[]
}

export type ReportPreferencesBottomSheetHandle = {
  present: (opts: {
    title: string
    description?: string
    users: {
      id: string
      firstName: string
      lastName: string
      email?: string
      emailHash: string | null
    }[]
    initial: ReportPreferences
    onSave: (prefs: ReportPreferences) => void
  }) => void
  dismiss: () => void
}

const ReportPreferencesBottomSheet =
  forwardRef<ReportPreferencesBottomSheetHandle>((_, ref) => {
    const { theme } = useUnistyles()
    const widescreenStyle = useWidescreenBottomSheetStyle()
    const modalRef = useRef<BottomSheetModal>(null)

    const [title, setTitle] = useState('')
    const [description, setDescription] = useState<string | undefined>('')
    const [users, setUsers] = useState<
      {
        id: string
        firstName: string
        lastName: string
        email?: string
        emailHash: string | null
      }[]
    >([])
    const [search, setSearch] = useState('')
    const [recipientUserIds, setRecipientUserIds] = useState<string[]>([])
    const onSaveRef = useRef<(prefs: ReportPreferences) => void>(() => {})

    const present: ReportPreferencesBottomSheetHandle['present'] = useCallback(
      (opts) => {
        setTitle(opts.title)
        setDescription(opts.description)
        setUsers(opts.users)
        setRecipientUserIds(opts.initial.recipientUserIds)
        setSearch('')
        onSaveRef.current = opts.onSave
        modalRef.current?.present()
      },
      []
    )

    const dismiss = useCallback(() => modalRef.current?.dismiss(), [])

    useImperativeHandle(ref, () => ({ present, dismiss }), [present, dismiss])

    const renderBackdrop = useCallback(
      (props: BottomSheetBackdropProps) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          pressBehavior="close"
        />
      ),
      []
    )

    const filteredUsers = useMemo(() => {
      const q = search.trim().toLowerCase()
      const sorted = [...users].sort((a, b) => {
        const an = `${a.firstName} ${a.lastName}`.toLowerCase()
        const bn = `${b.firstName} ${b.lastName}`.toLowerCase()
        return an.localeCompare(bn)
      })
      if (!q) return sorted
      return sorted.filter((u) => {
        const name = `${u.firstName} ${u.lastName}`.toLowerCase()
        return name.includes(q) || u.email?.toLowerCase().includes(q)
      })
    }, [users, search])

    const sections = useMemo(() => {
      const groups: Record<string, typeof users> = {}
      for (const u of filteredUsers) {
        const key = (u.firstName?.[0] || u.lastName?.[0] || '#').toUpperCase()
        if (!groups[key]) groups[key] = []
        groups[key].push(u)
      }
      return Object.keys(groups)
        .sort()
        .map((k) => ({ title: k, data: groups[k] }))
    }, [filteredUsers])

    const toggleRecipient = (userId: string) => {
      setRecipientUserIds((prev) =>
        prev.includes(userId)
          ? prev.filter((id) => id !== userId)
          : [...prev, userId]
      )
    }

    const handleSave = () => {
      onSaveRef.current?.({ recipientUserIds })
      dismiss()
    }

    return (
      <BottomSheetModal
        backdropComponent={renderBackdrop}
        enableDismissOnClose
        enableDynamicSizing
        handleIndicatorStyle={{ backgroundColor: theme.colors.onBackground }}
        handleStyle={{ backgroundColor: theme.colors.background }}
        index={0}
        keyboardBehavior="interactive"
        keyboardBlurBehavior="restore"
        ref={modalRef}
        style={widescreenStyle}
      >
        <BottomSheetView style={{ backgroundColor: theme.colors.background }}>
          <View style={styles.container}>
            <Text style={styles.title} variant="titleLarge">
              {title}
            </Text>
            {!!description && (
              <Text style={styles.subtitle} variant="bodySmall">
                {description}
              </Text>
            )}
            <View style={styles.searchContainer}>
              <View style={styles.searchIcon}>
                <Icon size={20} source="magnify" />
              </View>
              <BottomSheetTextInput
                onChangeText={setSearch}
                placeholder="Search people by name or email"
                style={styles.searchInput}
                value={search}
              />
            </View>
            {!!recipientUserIds.length && (
              <View style={styles.selectedContainer}>
                <View style={styles.selectedHeader}>
                  <Text style={styles.selectedLabel} variant="bodySmall">
                    Selected ({recipientUserIds.length})
                  </Text>
                  <Button compact onPress={() => setRecipientUserIds([])}>
                    Clear
                  </Button>
                </View>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={styles.chipRow}
                >
                  {recipientUserIds.map((id) => {
                    const user = users.find((u) => u.id === id)
                    if (!user) return null
                    return (
                      <UniChip
                        key={id}
                        onClose={() => toggleRecipient(id)}
                        style={styles.chip}
                      >
                        {user.firstName + ' ' + user.lastName}
                      </UniChip>
                    )
                  })}
                </ScrollView>
                <UniDivider style={styles.divider} />
              </View>
            )}
            <SectionList
              keyboardShouldPersistTaps="always"
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => {
                const checked = recipientUserIds.includes(item.id)
                return (
                  <TouchableRipple
                    accessibilityRole="button"
                    onPress={() => toggleRecipient(item.id)}
                    style={styles.userRow}
                  >
                    <>
                      <UserAvatar
                        size={32}
                        style={styles.avatar}
                        user={{
                          emailHash: item.emailHash || '',
                          firstName: item.firstName,
                          lastName: item.lastName,
                        }}
                      />
                      <View style={styles.userInfo}>
                        <Text style={styles.userName}>
                          {item.firstName} {item.lastName}
                        </Text>
                        {!!item.email && (
                          <Text style={styles.userEmail} variant="bodySmall">
                            {item.email}
                          </Text>
                        )}
                      </View>
                      <Checkbox
                        onPress={() => toggleRecipient(item.id)}
                        status={checked ? 'checked' : 'unchecked'}
                      />
                    </>
                  </TouchableRipple>
                )
              }}
              renderSectionHeader={({ section }) => (
                <Text style={styles.sectionHeader}>{section.title}</Text>
              )}
              sections={sections}
              stickySectionHeadersEnabled
              style={styles.scroll}
            />

            <View style={styles.actions}>
              <Button onPress={dismiss}>Cancel</Button>
              <Button mode="contained" onPress={handleSave}>
                Save
              </Button>
            </View>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    )
  })

ReportPreferencesBottomSheet.displayName = 'ReportPreferencesBottomSheet'

const styles = StyleSheet.create((theme) => ({
  actions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[2],
    justifyContent: 'flex-end',
    marginTop: theme.tokens.spacing[2],
  },
  avatar: {
    marginRight: theme.tokens.spacing[2],
  },
  chip: {
    marginRight: theme.tokens.spacing[1],
  },
  chipRow: {
    marginBottom: theme.tokens.spacing[1],
  },
  container: {
    padding: theme.tokens.spacing[4],
  },
  divider: {
    marginVertical: theme.tokens.spacing[1],
  },
  scroll: {
    maxHeight: 240,
  },
  searchContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.surfaceVariant,
    borderRadius: 50,
    flexDirection: 'row',
    height: 50,
    marginBottom: theme.tokens.spacing[1],
    marginTop: theme.tokens.spacing[1],
    paddingHorizontal: theme.tokens.spacing[4],
  },
  searchIcon: {
    marginRight: theme.tokens.spacing[1],
  },
  searchInput: {
    flex: 1,
    paddingVertical: theme.tokens.spacing[2],
  },
  sectionHeader: {
    color: theme.colors.onSurfaceVariant,
    fontWeight: '600',
    marginTop: theme.tokens.spacing[1],
  },
  selectedContainer: {
    marginTop: theme.tokens.spacing[1],
  },
  selectedHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectedLabel: {
    color: theme.colors.onSurfaceVariant,
  },
  subtitle: {
    color: theme.colors.onSurfaceVariant,
    marginTop: theme.tokens.spacing[1],
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  userEmail: {
    color: theme.colors.onSurfaceVariant,
  },
  userInfo: {
    flex: 1,
  },
  userName: {
    fontWeight: '500',
  },
  userRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.tokens.spacing[1],
  },
}))

export default ReportPreferencesBottomSheet
